import type { Clip, Overlay, Segment } from "./clip.js";
import { MIN_OVERLAY_MS, overlayInPieces, snapToPieces } from "./overlays.js";

/** How long a new picture shows when nothing says otherwise. */
export const DEFAULT_OVERLAY_MS = 3000;

/** The kept source moment nearest to `ms`: `ms` itself inside a piece, else the closest piece edge. */
export function nearestKept(segments: Segment[], ms: number): number | null {
  let best: number | null = null;
  for (const s of segments) {
    if (ms >= s.inMs && ms <= s.outMs) return ms;
    for (const edge of [s.inMs, s.outMs]) if (best == null || Math.abs(edge - ms) < Math.abs(best - ms)) best = edge;
  }
  return best;
}

/** The stretches of kept footage no picture covers yet, in source ms (each inside one piece). */
export function freeStretches(segments: Segment[], overlays: Pick<Overlay, "startMs" | "endMs" | "enabled">[]): Segment[] {
  const taken = overlays.filter((o) => o.enabled !== false);
  const out: Segment[] = [];
  for (const s of segments) {
    let cursor = s.inMs;
    for (const o of [...taken].sort((a, b) => a.startMs - b.startMs)) {
      if (o.endMs <= cursor || o.startMs >= s.outMs) continue;
      if (o.startMs > cursor) out.push({ inMs: cursor, outMs: o.startMs });
      cursor = Math.max(cursor, o.endMs);
    }
    if (cursor < s.outMs) out.push({ inMs: cursor, outMs: s.outMs });
  }
  return out.filter((r) => r.outMs - r.inMs >= MIN_OVERLAY_MS);
}

/**
 * A place for a new picture of about `durMs`: the free stretch nearest `nearMs` (the middle of the speech when none is given) that
 * fits it, else the longest free stretch (shortened to fit), else the piece nearest the wish, over whatever is there.
 */
export function freeSpot(segments: Segment[], overlays: Pick<Overlay, "startMs" | "endMs" | "enabled">[], durMs = DEFAULT_OVERLAY_MS, nearMs?: number): { startMs: number; endMs: number } | null {
  if (!segments.length) return null;
  const mid = nearMs ?? (segments[0].inMs + (segments[segments.length - 1].outMs - segments[0].inMs) / 2);
  const free = freeStretches(segments, overlays);
  const fits = free.filter((r) => r.outMs - r.inMs >= durMs);
  const dist = (r: Segment) => (mid < r.inMs ? r.inMs - mid : mid > r.outMs - durMs ? mid - (r.outMs - durMs) : 0);
  if (fits.length) {
    const r = fits.sort((a, b) => dist(a) - dist(b))[0];
    const startMs = Math.round(Math.max(r.inMs, Math.min(r.outMs - durMs, mid)));
    return { startMs, endMs: startMs + durMs };
  }
  if (free.length) { const r = free.sort((a, b) => b.outMs - b.inMs - (a.outMs - a.inMs))[0]; return { startMs: r.inMs, endMs: r.outMs }; }
  const at = nearestKept(segments, mid);
  if (at == null) return null;
  const seg = segments.find((s) => at >= s.inMs && at <= s.outMs)!;
  const startMs = Math.round(Math.max(seg.inMs, Math.min(seg.outMs - Math.min(durMs, seg.outMs - seg.inMs), at)));
  return { startMs, endMs: Math.min(seg.outMs, startMs + durMs) };
}

/**
 * Where a wished-for range lands: pulled into the kept pieces when any of it is kept; otherwise moved whole to the nearest kept
 * spot (keeping its length), so a picture never sits where nothing plays.
 */
export function placeOverlay(segments: Segment[], overlays: Pick<Overlay, "startMs" | "endMs" | "enabled">[], want: { startMs: number; endMs: number }): { startMs: number; endMs: number } | null {
  const dur = Math.max(MIN_OVERLAY_MS, want.endMs - want.startMs);
  const snapped = snapToPieces(segments, want.startMs, want.endMs);
  if (snapped && snapped.endMs - snapped.startMs >= MIN_OVERLAY_MS) return snapped;
  return freeSpot(segments, overlays, Math.min(dur, DEFAULT_OVERLAY_MS), nearestKept(segments, want.startMs) ?? undefined);
}

/** A clip whose pictures all sit in kept footage: one left outside by a trim or a cut is moved to the nearest kept spot. */
export function healOverlays<C extends Pick<Clip, "segments" | "overlays">>(clip: C): C {
  if (!clip.segments.length || !clip.overlays.length) return clip;
  let changed = false;
  const overlays = clip.overlays.map((o) => {
    if (overlayInPieces(clip.segments, o)) return o;
    const at = placeOverlay(clip.segments, clip.overlays.filter((x) => x !== o), o);
    if (!at || (at.startMs === o.startMs && at.endMs === o.endMs)) return o;
    changed = true;
    return { ...o, startMs: at.startMs, endMs: at.endMs };
  });
  return changed ? { ...clip, overlays } : clip;
}
